import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { X } from 'lucide-react';

/**
 * AdminDialog - Core Compound Modal Component for Reseller Bazzar Admin Panel.
 * Built strictly on top of Material UI with WCAG 2.1 AA accessibility and responsive scaling.
 */
export function AdminDialog({
  open,
  onClose,
  maxWidth = 'sm',
  fullWidth = true,
  fullScreenOnMobile = false,
  scroll = 'paper',
  children,
  ariaLabelledBy,
  ariaDescribedBy,
  disableEscapeKeyDown = false,
  slotProps = {},
  sx = {},
  ...rest
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isFullScreen = fullScreenOnMobile && isMobile;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={isFullScreen}
      scroll={scroll}
      disableEscapeKeyDown={disableEscapeKeyDown}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      slotProps={{
        backdrop: {
          transitionDuration: 200,
          ...slotProps.backdrop,
        },
        paper: {
          sx: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'all 0.2s ease-in-out',
            ...slotProps.paper?.sx,
          },
          ...slotProps.paper,
        },
        ...slotProps,
      }}
      sx={{
        '& .MuiDialog-paper': {
          ...(isFullScreen ? { m: 0, borderRadius: 0 } : {}),
        },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Dialog>
  );
}

/**
 * AdminDialogHeader - Consistent dialog header with icon, title, subtitle, status badge, and accessible close button.
 */
export function AdminDialogHeader({
  id,
  title,
  subtitle,
  icon: IconComponent,
  iconColor = 'primary', // 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | custom hex
  badge,
  badgeColor = 'default',
  actions,
  onClose,
  closeLabel = 'Close dialog',
  disableClose = false,
  sx = {},
}) {
  const theme = useTheme();

  // Resolve palette color
  const paletteColor = theme.palette[iconColor] || {
    main: typeof iconColor === 'string' && iconColor.startsWith('#') ? iconColor : theme.palette.primary.main,
    lighter: alpha(
      typeof iconColor === 'string' && iconColor.startsWith('#') ? iconColor : theme.palette.primary.main,
      0.12
    ),
  };

  return (
    <DialogTitle
      id={id}
      component="div"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        p: { xs: 2, sm: 2.5 },
        px: { xs: 2.5, sm: 3 },
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        zIndex: 1,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0, flex: 1 }}>
        {IconComponent && (
          <Box
            sx={{
              width: { xs: 38, sm: 42 },
              height: { xs: 38, sm: 42 },
              borderRadius: '10px',
              bgcolor: paletteColor.lighter || alpha(paletteColor.main, 0.12),
              color: paletteColor.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {React.isValidElement(IconComponent) ? (
              IconComponent
            ) : (
              <IconComponent size={20} strokeWidth={2.2} />
            )}
          </Box>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.025rem', sm: '1.15rem' },
                letterSpacing: '-0.015em',
                color: 'text.primary',
                lineHeight: 1.25,
              }}
            >
              {title}
            </Typography>
            {badge && (
              <Chip
                size="small"
                label={badge}
                color={badgeColor}
                sx={{ height: 22, fontSize: '0.725rem', fontWeight: 700 }}
              />
            )}
          </Box>

          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                mt: 0.35,
                lineHeight: 1.4,
                fontSize: { xs: '0.775rem', sm: '0.8125rem' },
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {actions}
        {onClose && (
          <IconButton
            aria-label={closeLabel}
            onClick={onClose}
            disabled={disableClose}
            size="small"
            sx={{
              color: 'text.secondary',
              p: 0.75,
              borderRadius: 1.5,
              transition: 'all 0.15s ease-in-out',
              '&:hover': {
                bgcolor: alpha(theme.palette.text.primary, 0.06),
                color: 'text.primary',
              },
            }}
          >
            <X size={19} />
          </IconButton>
        )}
      </Box>
    </DialogTitle>
  );
}

/**
 * AdminDialogContent - Scrollable content area with consistent spacing and sleek scrollbar.
 */
export function AdminDialogContent({
  children,
  dividers = false,
  sx = {},
  ...rest
}) {
  return (
    <DialogContent
      dividers={dividers}
      sx={{
        p: { xs: 2.5, sm: 3 },
        overflowY: 'auto',
        color: 'text.primary',
        ...sx,
      }}
      {...rest}
    >
      {children}
    </DialogContent>
  );
}

/**
 * AdminDialogActions - Fixed or sticky actions container at the bottom of the dialog.
 */
export function AdminDialogActions({
  children,
  sx = {},
  ...rest
}) {
  return (
    <DialogActions
      sx={{
        p: { xs: 2, sm: 2.5 },
        px: { xs: 2.5, sm: 3 },
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: (theme) =>
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.default, 0.4)
            : alpha(theme.palette.grey[50], 0.8),
        gap: 1.25,
        justifyContent: 'flex-end',
        ...sx,
      }}
      {...rest}
    >
      {children}
    </DialogActions>
  );
}

AdminDialog.Header = AdminDialogHeader;
AdminDialog.Content = AdminDialogContent;
AdminDialog.Actions = AdminDialogActions;

export default AdminDialog;
